import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import useHelps from "../../hooks/useHelps";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", padding: theme.spacing(1, 0) },
  header: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  title: { fontWeight: 700, fontSize: "1.05rem" },
  sub: { color: theme.palette.text.secondary, fontSize: "0.82rem" },
  list: { display: "flex", flexDirection: "column", gap: theme.spacing(1.25) },
  item: {
    display: "grid",
    gridTemplateColumns: "128px minmax(0, 1fr) auto",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(1.25),
    borderRadius: 10,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "96px minmax(0, 1fr)" },
  },
  thumb: {
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    borderRadius: 6,
    display: "block",
    backgroundColor: theme.palette.action.hover,
  },
  itemTitle: { fontWeight: 600 },
  itemDesc: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  itemAction: { [theme.breakpoints.down("xs")]: { gridColumn: "1 / -1" } },
  drawerPaper: { width: "min(520px, 100%)", padding: theme.spacing(2.5) },
  preview: { maxWidth: 320, marginTop: theme.spacing(1) },
  footer: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(3), flexWrap: "wrap" },
}));

const EMPTY = { title: "", video: "", description: "" };

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_URL =
  /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&#/].*)?$/;

// Aceita o link inteiro ou só o código e devolve o código de 11 caracteres.
// É a mesma regra do backend, que guarda apenas o código.
export const extractYoutubeId = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (YOUTUBE_ID.test(text)) return text;
  const match = text.match(YOUTUBE_URL);
  return match ? match[1] : null;
};

const HelpSchema = Yup.object().shape({
  title: Yup.string().trim().min(3, "Use ao menos 3 caracteres").max(120, "Máximo de 120 caracteres").required("Informe o título"),
  description: Yup.string().max(500, "Máximo de 500 caracteres"),
  video: Yup.string()
    .required("Informe o link do vídeo")
    .test("youtube", "Link inválido. Cole o endereço do vídeo no YouTube.", (v) => Boolean(extractYoutubeId(v))),
});

const thumbUrl = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;

function HelpForm({ initialValue, loading, onSubmit, onCancel, onDelete }) {
  const classes = useStyles();
  const isEditing = initialValue.id !== undefined;

  return (
    <Formik enableReinitialize initialValues={initialValue} validationSchema={HelpSchema} onSubmit={onSubmit}>
      {({ values, handleChange, handleBlur, touched, errors }) => {
        const id = extractYoutubeId(values.video);
        return (
          <Form>
            <Typography className={classes.title}>{isEditing ? "Editar vídeo" : "Novo vídeo"}</Typography>
            <TextField
              name="title"
              label="Título"
              variant="outlined"
              margin="normal"
              fullWidth
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.title && Boolean(errors.title)}
              helperText={touched.title && errors.title}
            />
            <TextField
              name="video"
              label="Link do YouTube"
              variant="outlined"
              margin="dense"
              fullWidth
              value={values.video}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.video && Boolean(errors.video)) || (Boolean(values.video) && id === null)}
              helperText={
                id === null
                  ? "Link inválido. Cole o endereço do vídeo no YouTube."
                  : id
                  ? "Vídeo reconhecido."
                  : "Cole o link do vídeo (ou só o código de 11 caracteres)."
              }
            />
            {id && (
              <img className={`${classes.thumb} ${classes.preview}`} src={thumbUrl(id)} alt="Miniatura do vídeo" />
            )}
            <TextField
              name="description"
              label="Descrição"
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              rows={3}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />
            <div className={classes.footer}>
              {isEditing && (
                <Button color="secondary" variant="outlined" disabled={loading} onClick={() => onDelete(initialValue)}>
                  Excluir
                </Button>
              )}
              <Box flex={1} />
              <Button variant="outlined" onClick={onCancel}>Cancelar</Button>
              <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                Salvar
              </ButtonWithSpinner>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default function HelpsManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useHelps();

  const [records, setRecords] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(EMPTY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    setListLoading(true);
    try {
      const helpList = await list();
      setRecords(Array.isArray(helpList) ? helpList : []);
    } catch (e) {
      toastError(e);
    }
    setListLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    setDrawerOpen(false);
    setRecord(EMPTY);
  };

  const handleEdit = (data) => {
    setRecord({
      id: data.id,
      title: data.title || "",
      description: data.description || "",
      video: data.video || "",
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // O backend guarda só o código do vídeo.
      const payload = { ...values, video: extractYoutubeId(values.video) || "" };
      if (payload.id !== undefined) {
        await update(payload);
      } else {
        await save(payload);
      }
      await load();
      close();
      toast.success("Vídeo salvo.");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      setConfirmDelete(false);
      await load();
      close();
      toast.success("Vídeo excluído.");
    } catch (e) {
      setConfirmDelete(false);
      toastError(e);
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Box>
          <Typography className={classes.title}>Vídeos de ajuda</Typography>
          <Typography className={classes.sub}>
            Aparecem para todas as empresas na página Ajuda, em ordem alfabética pelo título.
          </Typography>
        </Box>
        <Box flex={1} />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setRecord(EMPTY);
            setDrawerOpen(true);
          }}
        >
          + Novo vídeo
        </Button>
      </div>

      <div className={classes.list}>
        {listLoading &&
          [0, 1, 2].map((i) => <Skeleton key={i} variant="rect" height={86} style={{ borderRadius: 10 }} />)}
        {!listLoading &&
          records.map((row) => (
            <Paper key={row.id} variant="outlined" className={classes.item}>
              {row.video ? (
                <img className={classes.thumb} src={thumbUrl(row.video)} alt="" loading="lazy" />
              ) : (
                <div className={classes.thumb} />
              )}
              <div style={{ minWidth: 0 }}>
                <Typography className={classes.itemTitle}>{row.title}</Typography>
                {row.description && <Typography className={classes.itemDesc}>{row.description}</Typography>}
              </div>
              <div className={classes.itemAction}>
                <Button size="small" variant="outlined" onClick={() => handleEdit(row)}>Editar</Button>
              </div>
            </Paper>
          ))}
        {!listLoading && records.length === 0 && (
          <Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>
            Nenhum vídeo cadastrado. Use "Novo vídeo" para adicionar o primeiro.
          </Typography>
        )}
      </div>

      <Drawer anchor="right" open={drawerOpen} onClose={close} classes={{ paper: classes.drawerPaper }}>
        <HelpForm
          initialValue={record}
          loading={loading}
          onSubmit={handleSubmit}
          onCancel={close}
          onDelete={() => setConfirmDelete(true)}
        />
      </Drawer>

      <ConfirmationModal
        title="Excluir vídeo"
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      >
        {`Excluir "${record.title || ""}"? Ele some da página Ajuda de todas as empresas.`}
      </ConfirmationModal>
    </div>
  );
}
